import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Select, Spin } from 'antd';
import { DefaultOptionType, SelectProps } from 'antd/lib/select';
import cs from 'classnames';

import { filterOptions } from 'components/Filters/utils/filterOptions';
import { mapOptionsWithLabels } from 'components/Filters/utils/mapOptionsWithLabels';

import getPopupContainer from 'utils/getPopupContainer';

import { ElasticSearchFacetCounts } from 'types/commons';
import { FormattedOption } from 'types/dictionaries';

import { UPGRADE_TIER_TOOLTIP } from 'constants/uiSettings';

import { SelectValue } from '../../filter.types';
import TooltipAccessWrapper from '../TooltipAccessWrapper';
import styles from './SelectCommon.module.scss';

const { Option } = Select;

interface SelectCommonProps extends Omit<
  SelectProps<SelectValue>,
  'value' | 'onChange'
> {
  title: string;
  value?: SelectValue | undefined;
  onChange?: (value: SelectValue) => void;
  options?: FormattedOption[];
  loading?: boolean;
  singleMode?: boolean;
  customOptions?: ReactNode;
  errorText?: string;
  isSearchByLabel?: boolean;
  disabledOptionsValues?: string[];
  hasAccessToFilter?: boolean;
  onCheckboxCountrySelect?: () => void;
  disabled?: boolean;
  facets?: ElasticSearchFacetCounts;
  className?: string;
  isShowAllTags?: boolean;
}

const toSelectedValues = (selectValue?: SelectValue): string[] | undefined =>
  Array.isArray(selectValue) ? (selectValue as string[]) : undefined;

const hasSelectedValue = (selectValue?: SelectValue): boolean =>
  Array.isArray(selectValue) ? selectValue.length > 0 : Boolean(selectValue);

const toSearchText = (input: unknown): string =>
  String(input ?? '').toLowerCase();

const filterPlainText = (option: FormattedOption): string =>
  option.filterLabel ?? (typeof option.label === 'string' ? option.label : '');

const SelectCommon: FC<SelectCommonProps> = ({
  title,
  value,
  onChange,
  options,
  loading = false,
  singleMode = false,
  customOptions,
  status,
  errorText,
  isSearchByLabel = true,
  hasAccessToFilter = false,
  disabledOptionsValues = [],
  onCheckboxCountrySelect,
  disabled = false,
  facets,
  className,
  isShowAllTags = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const preparedOptions = useMemo(() => {
    if (!options) return options;

    const optionsWithLabels = mapOptionsWithLabels(options, facets);

    return filterOptions(toSelectedValues(value), optionsWithLabels, facets);
  }, [options, facets, value]);

  const searchByLabel = (input: string, option?: DefaultOptionType) => {
    const normalizedLabel = toSearchText(
      typeof option?.label === 'string' ? option.label : ''
    );
    const normalizedInput = toSearchText(input);
    return normalizedLabel.includes(normalizedInput);
  };

  const handleChange = (newValue: SelectValue): void => {
    onChange?.(newValue);
    setSearchValue('');
  };

  const handleCheckboxChange = (checked: boolean, optionValue: string) => {
    if (singleMode) {
      handleChange(checked ? optionValue : '');
    } else {
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = checked
        ? [...currentValue, optionValue]
        : currentValue.filter((val) => val !== optionValue);
      handleChange(newValue);
    }

    onCheckboxCountrySelect?.();
  };

  const visibleOptions = useMemo(() => {
    if (!preparedOptions) return preparedOptions;

    if (!searchValue.trim()) return preparedOptions;

    const search = toSearchText(searchValue);

    return preparedOptions.filter((option) =>
      toSearchText(filterPlainText(option)).includes(search)
    );
  }, [preparedOptions, searchValue]);

  const hasSearchValue = Boolean(searchValue.trim());
  const hasSearchWithNoMatches = hasSearchValue && !visibleOptions?.length;

  const dropdownRender = () => (
    <div className={styles.optionsWrapper}>
      {hasSearchWithNoMatches ? (
        <div className={styles.emptyState}>{t('common.noData')}</div>
      ) : (
        visibleOptions?.map((option) => {
          const isChecked = singleMode
            ? value === option.value
            : Array.isArray(value) && value.includes(option.value);

          return (
            <div
              key={option.value}
              className={cs(styles.optionWrapper, {
                [styles.optionWrapperChecked]: isChecked
              })}
            >
              <Checkbox
                className={styles.optionCheckbox}
                checked={isChecked}
                onChange={(e) =>
                  handleCheckboxChange(e.target.checked, option.value)
                }
              >
                {option.label}
              </Checkbox>
            </div>
          );
        })
      )}
    </div>
  );

  const customDropdownRender =
    !singleMode && !customOptions ? dropdownRender : undefined;

  const shouldHideByFacets =
    Boolean(facets) &&
    !preparedOptions?.length &&
    !hasSelectedValue(value) &&
    !hasSearchValue;

  if (shouldHideByFacets) {
    return null;
  }

  return (
    <div className="item-container">
      <div className={cs('item-title', { disabled: disabled })}>{title}</div>
      <TooltipAccessWrapper
        hasAccessToFilter={hasAccessToFilter}
        message={t(UPGRADE_TIER_TOOLTIP)}
      >
        <Select
          mode={singleMode ? undefined : 'multiple'}
          className={cs({ [styles.allTags]: isShowAllTags }, [className])}
          maxTagCount={isShowAllTags ? undefined : 'responsive'}
          value={value}
          onChange={handleChange}
          loading={loading}
          filterOption={isSearchByLabel ? searchByLabel : false}
          status={status}
          notFoundContent={
            loading ? (
              <div className="select-spinner-container">
                <Spin size="default" />
              </div>
            ) : hasSearchWithNoMatches ? (
              t('common.noData')
            ) : null
          }
          getPopupContainer={getPopupContainer}
          disabled={disabled || !hasAccessToFilter}
          popupRender={customDropdownRender}
          onSearch={setSearchValue}
          {...props}
        >
          {customOptions ||
            (visibleOptions &&
              visibleOptions.map((optionItem) => (
                <Option
                  key={optionItem.value}
                  value={optionItem.value}
                  label={filterPlainText(optionItem)}
                  disabled={disabledOptionsValues.includes(optionItem.value)}
                >
                  {optionItem.label}
                </Option>
              )))}
        </Select>
        {status === 'error' && errorText && (
          <div className="select-error-text">{errorText}</div>
        )}
      </TooltipAccessWrapper>
    </div>
  );
};

export default SelectCommon;
